// Client/src/pages/digi/Invoice/TemplateDesigner/FieldPicker.jsx
//
// Searchable, grouped list of the fields a template can print. Used by the
// Fields rail panel, the "Insert field" menu and the {{ autocomplete.

import React, { useEffect, useMemo, useRef, useState } from "react";
import FeatherIcon from "feather-icons-react";

export function filterFields(groups, query) {
  const q = (query || "").trim().toLowerCase();
  return (groups || [])
    .map((g) => ({
      group: g.group,
      fields: g.fields.filter((f) => !q || f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q)),
    }))
    .filter((g) => g.fields.length);
}

/**
 * The list itself. `onPick(field)`; `activeKey` highlights the keyboard choice.
 * `draggable` makes chips draggable as text/plain "{{Label}}" (drop into any
 * text input).
 */
export function FieldList({ groups, onPick, activeKey, draggable, samples }) {
  return (
    <div className="td-fieldlist" role="listbox" aria-label="Fields">
      {groups.map((g) => (
        <div key={g.group} className="td-fieldlist__group" role="group" aria-label={g.group}>
          <div className="td-fieldlist__title">{g.group}</div>
          {g.fields.map((f) => (
            <button
              key={f.key}
              type="button"
              role="option"
              aria-selected={activeKey === f.key}
              className={`td-chip td-chip--field${activeKey === f.key ? " is-active" : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(f)}
              draggable={draggable}
              onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", `{{${f.label}}}`);
                e.dataTransfer.setData("application/x-td-field", f.key);
              }}
              title={samples?.[f.key] ? `e.g. ${samples[f.key]}` : f.key}
            >
              <FeatherIcon icon="hash" size={11} />
              <span>{f.label}</span>
            </button>
          ))}
        </div>
      ))}
      {!groups.length && <div className="td-empty">No fields match.</div>}
    </div>
  );
}

/** A small search box + list, used inside popovers and the rail. */
export default function FieldPicker({ groups, onPick, autoFocus, draggable, initialQuery = "" }) {
  const [query, setQuery] = useState(initialQuery);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const filtered = useMemo(() => filterFields(groups, query), [groups, query]);
  const flat = useMemo(() => filtered.flatMap((g) => g.fields), [filtered]);
  useEffect(() => setActive(0), [query]);
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);
  return (
    <div className="td-fieldpicker">
      <div className="td-search">
        <FeatherIcon icon="search" size={14} />
        <input
          ref={inputRef}
          className="td-input"
          placeholder="Search fields"
          aria-label="Search fields"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((a) => Math.min(flat.length - 1, a + 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => Math.max(0, a - 1));
            } else if (e.key === "Enter" && flat[active]) {
              e.preventDefault();
              onPick(flat[active]);
            }
          }}
        />
      </div>
      <FieldList groups={filtered} onPick={onPick} activeKey={flat[active]?.key} draggable={draggable} />
    </div>
  );
}
